import type { ReactNode } from 'react'
import {
  Boxes,
  CheckCircle2,
  ChevronDown,
  Circle,
  CircleDot,
  ClipboardCheck,
  ScrollText,
  SkipForward,
  UserRoundPlus,
  Wrench,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AssigneeSelect } from '@/components/procedure/AssigneeSelect'
import { PickListActionsMenu } from '@/components/pick-lists/PickListActionsMenu'
import { OverduePill } from '@/components/procedures/OverduePill'
import {
  EXPIRED_BADGE,
  currentStepName,
  equipmentIconState,
  inventoryIconState,
  leadItemsFor,
  needsReservation,
  resourceIconTooltip,
  shortDueDate,
} from '@/lib/board/boardCardView'
import type { BoardPrefs } from '@/lib/board/boardPrefs'
import type { BoardRun } from '@/lib/board/boardRuns'
import { assigneeInitials } from '@/lib/assignedTo'
import { STATUS_META } from '@/lib/journey'
import { isOverdue } from '@/lib/procedureDates'
import { PROCEDURE_TYPE_COLOR } from '@/lib/procedureTypeColors'
import { timeAgo } from '@/lib/timeAgo'
import { cn } from '@/lib/utils'
import { PROCEDURE_TYPE_LABELS, type ProcedureAssignee, type ProcedureSummaryStep } from '@/types/cgt'

export interface BoardCardAccess {
  canAssign: boolean
  /** Reserve: allowed for this run right now. */
  canReserve: boolean
  /** Why Reserve is unavailable, shown on hover when `canReserve` is false. */
  reserveBlockedReason: string | null
  canViewProducts: boolean
  canViewCapa: boolean
  canReadAudit: boolean
}

const ICON_BUTTON = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors disabled:cursor-default'

/** A status badge, not a button: Reserve is the card's one reservation action. */
function StatusIcon({ label, className, children }: { label: string; className: string; children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span role='img' tabIndex={0} aria-label={label} className={cn(ICON_BUTTON, 'cursor-help', className)}>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const STEP_STATUS_LABEL: Record<ProcedureSummaryStep['status'], string> = {
  completed: 'Done',
  skipped: 'Skipped',
  in_progress: 'In progress',
  not_started: 'Not started',
}

function StepMark({ status }: { status: ProcedureSummaryStep['status'] }) {
  if (status === 'completed') return <CheckCircle2 className='mt-px h-3.5 w-3.5 shrink-0 text-emerald-600' />
  if (status === 'in_progress') return <CircleDot className='mt-px h-3.5 w-3.5 shrink-0 text-blue-600' />
  if (status === 'skipped') return <SkipForward className='mt-px h-3.5 w-3.5 shrink-0 text-slate-400' />
  return <Circle className='mt-px h-3.5 w-3.5 shrink-0 text-slate-300' />
}

function ProgressBar({ pct, completed, total }: { pct: number; completed: number; total: number }) {
  return (
    <>
      <div className='h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200'>
        <div className='h-full rounded-full bg-emerald-500' style={{ width: `${pct}%` }} />
      </div>
      <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>
        {completed}/{total}
      </span>
    </>
  )
}

/**
 * One procedure on the Overall Status board.
 *
 * Header: template, due date, type (the colour identifies the type). Body:
 * assignment, the type's identifying line, P/H, progress, status, age. Bottom
 * bar: inventory, pick list, equipment, Reserve, CAPA, audit trail. Every
 * optional element respects the user's card settings and their permissions.
 */
export function ProcedureBoardCard({
  run,
  prefs,
  access,
  users,
  assigning,
  today,
  menuOpen,
  onMenuOpenChange,
  onAssign,
  onOpen,
  onReserve,
  onProducts,
  onHistory,
  onCapa,
  onAudit,
}: {
  run: BoardRun
  prefs: BoardPrefs
  access: BoardCardAccess
  users: ProcedureAssignee[]
  assigning: boolean
  today: string
  menuOpen: boolean
  onMenuOpenChange: (open: boolean) => void
  onAssign: (assigneeId: string | null) => void
  onOpen: () => void
  onReserve: () => void
  onProducts: () => void
  onHistory: () => void
  onCapa: () => void
  onAudit: () => void
}) {
  const { procedure: p, type } = run
  const summary = p.summary
  const color = PROCEDURE_TYPE_COLOR[type]
  // A cleaning run whose task's day has passed undone reads Expired (z8nrz7f9u5).
  const expired = run.boardStatus === 'expired'
  const meta = expired ? EXPIRED_BADGE : STATUS_META[run.stepStatus]
  const currentStep = currentStepName(summary?.steps)
  const pct = run.total > 0 ? Math.round((run.completed / run.total) * 100) : 0
  const due = shortDueDate(p.dueDate)
  const overdue = run.boardStatus !== 'complete' && isOverdue(p.dueDate, p.status, today)
  const assignee = p.assignedTo?.kind === 'user' ? p.assignedTo.user : null
  const assigneeLabel = assignee ? assignee.name : p.assignedTo?.kind === 'multiple' ? 'Multiple' : 'Unassigned'
  const lead = leadItemsFor({
    type,
    patientMrn: p.patientMrn,
    patientName: run.patientName,
    mfgNumber: p.mfgNumber,
    quantity: p.quantity ?? 1,
    workOrderNumber: p.workOrderNumber,
  })

  const inventory = prefs.show.inventory ? inventoryIconState(summary?.inventory) : 'hidden'
  const equipment = prefs.show.equipment ? equipmentIconState(summary?.equipment) : 'hidden'
  const reservable =
    (prefs.show.inventory || prefs.show.equipment) &&
    needsReservation(run.boardStatus, summary?.inventory, summary?.equipment)
  const canReserveNow = access.canReserve && reservable
  const reserveTitle = access.canReserve ? 'Reserve inventory and equipment' : (access.reserveBlockedReason ?? undefined)
  const showProduct = prefs.show.productHistory && access.canViewProducts && !!summary?.actions.product
  const showHistory = prefs.show.productHistory && !!summary?.actions.history && !!p.patientMrn && type !== 'KitCreation'
  const capaCount = summary?.capa.count ?? 0
  const showCapa = prefs.show.capa && access.canViewCapa && capaCount > 0 && !!summary?.capa.latestSubmissionId
  const showMenu = p.hasProcessStep !== false

  return (
    <article className='flex min-w-0 flex-col overflow-hidden rounded-lg border bg-background shadow-sm'>
      <header
        className='border-t-4 px-3 py-2'
        style={{ borderTopColor: color, backgroundColor: `${color}1f` }}
      >
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={onOpen}
            title={p.procedureTemplate.name}
            className='min-w-0 flex-1 truncate text-left text-sm font-semibold text-foreground hover:underline'
          >
            {p.procedureTemplate.name}
          </button>
          {due && (
            <span className='shrink-0 text-xs font-medium text-foreground' title='Due date'>
              {due}
            </span>
          )}
        </div>
        <div className='mt-0.5 flex items-center gap-2'>
          <span className='flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground'>
            <span className='h-2 w-2 rounded-full' style={{ backgroundColor: color }} />
            {PROCEDURE_TYPE_LABELS[type]}
          </span>
          {overdue && <OverduePill />}
        </div>
      </header>

      <div className='flex flex-1 flex-col gap-2.5 px-3 py-3'>
        <div className='flex items-start gap-2'>
          {access.canAssign ? (
            <AssigneeSelect
              variant='avatar'
              users={users}
              value={assignee}
              placeholder={assigneeLabel}
              disabled={assigning}
              onChange={onAssign}
            />
          ) : (
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                assignee ? 'bg-blue-600 text-white' : 'border border-dashed border-slate-300 text-slate-400',
              )}
            >
              {assignee ? assigneeInitials(assignee.name) : <UserRoundPlus className='h-3.5 w-3.5' />}
            </span>
          )}
          <div className='min-w-0 flex-1'>
            <div
              className={cn('truncate text-xs', assignee ? 'font-medium' : 'text-muted-foreground')}
              title={assigneeLabel}
            >
              {assigneeLabel}
            </div>
            {lead.length > 0 && (
              <div className='mt-0.5 truncate text-xs text-muted-foreground' title={lead.map(i => `${i.label} ${i.value}`).join(' · ')}>
                {lead.map((item, i) => (
                  <span key={item.label}>
                    {i > 0 && ' · '}
                    <span>{item.label} </span>
                    <span className='font-mono text-foreground'>{item.value}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          {(showProduct || showHistory) && (
            <div className='flex shrink-0 items-center gap-1'>
              {showProduct && (
                <button
                  type='button'
                  onClick={onProducts}
                  title='Products created in this procedure'
                  className='rounded-md px-1.5 py-0.5 text-sm font-bold text-blue-700 hover:bg-blue-50'
                >
                  P <span className='font-medium'>({summary?.productCount ?? 0})</span>
                </button>
              )}
              {showHistory && (
                <button
                  type='button'
                  onClick={onHistory}
                  title="The patient's other procedures"
                  className='rounded-md px-1.5 py-0.5 text-sm font-bold text-blue-700 hover:bg-blue-50'
                >
                  H <span className='font-medium'>({summary?.historyCount ?? 0})</span>
                </button>
              )}
            </div>
          )}
        </div>

        {run.stepStatus === 'pending_review' ? (
          // A form of the run is completed and waiting for its reviews
          // (z8nrz7dt41): that IS where the run stands, so the board says so in
          // red rather than naming the form — the client's "we can make it RED
          // for Pending Review". The form waiting is in the tooltip.
          <div
            className='truncate text-xs'
            title={currentStep ? `${currentStep} is waiting for review` : 'A form is waiting for review'}
          >
            <span className='text-muted-foreground'>Current step: </span>
            <span className='rounded bg-red-600 px-1.5 py-0.5 font-semibold text-white'>Pending Review</span>
          </div>
        ) : (
          currentStep && (
            <div className='truncate text-xs' title={currentStep}>
              <span className='text-muted-foreground'>Current step: </span>
              <span className='font-medium text-foreground'>{currentStep}</span>
            </div>
          )
        )}

        {summary?.steps && summary.steps.length > 0 ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type='button'
                title='See all steps'
                className='flex w-full items-center gap-2 rounded-md py-0.5 text-left hover:bg-muted'
              >
                <ProgressBar pct={pct} completed={run.completed} total={run.total} />
                <ChevronDown className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
              </button>
            </PopoverTrigger>
            <PopoverContent align='start' className='w-64 p-3'>
              <p className='mb-2 text-xs font-semibold text-muted-foreground'>Steps</p>
              <ol className='space-y-1.5'>
                {summary.steps.map((step, i) => (
                  <li key={`${i}-${step.name}`} className='flex items-start gap-2 text-xs'>
                    <StepMark status={step.status} />
                    <span
                      className={cn(
                        'min-w-0 flex-1',
                        step.status === 'completed' && 'text-muted-foreground',
                        step.name === currentStep && step.status !== 'completed' && 'font-semibold',
                      )}
                    >
                      {step.name}
                    </span>
                    <span className='shrink-0 text-[11px] text-muted-foreground'>{STEP_STATUS_LABEL[step.status]}</span>
                  </li>
                ))}
              </ol>
            </PopoverContent>
          </Popover>
        ) : (
          <div className='flex items-center gap-2'>
            <ProgressBar pct={pct} completed={run.completed} total={run.total} />
          </div>
        )}

        <div className='flex items-center justify-between gap-2'>
          <span
            className={cn(
              'inline-flex min-w-0 items-center gap-1.5 truncate rounded-full border px-2 py-0.5 text-xs font-medium',
              meta.badge,
            )}
          >
            <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', meta.dot)} />
            {expired ? EXPIRED_BADGE.label : run.stepLabel}
          </span>
          <span className='shrink-0 text-xs text-muted-foreground' title='Since the procedure started'>
            {timeAgo(p.startedAt)}
          </span>
        </div>
      </div>

      <footer className='flex flex-wrap items-center gap-1.5 border-t bg-muted/20 px-2 py-1.5'>
        {inventory !== 'hidden' && summary && (
          <StatusIcon
            label={resourceIconTooltip('Inventory', summary.inventory, canReserveNow)}
            className={inventory === 'held' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}
          >
            <Boxes className='h-4 w-4' />
          </StatusIcon>
        )}
        {showMenu && (
          <PickListActionsMenu
            scope={{ scope: 'PROCEDURE', procedureId: p.id }}
            open={menuOpen}
            onOpenChange={onMenuOpenChange}
          />
        )}
        {equipment !== 'hidden' && summary && (
          <StatusIcon
            label={resourceIconTooltip('Equipment', summary.equipment, canReserveNow)}
            className={equipment === 'held' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}
          >
            <Wrench className='h-4 w-4' />
          </StatusIcon>
        )}
        {reservable && (
          <Button size='sm' className='h-7 px-3' onClick={onReserve} disabled={!access.canReserve} title={reserveTitle}>
            Reserve
          </Button>
        )}
        {showCapa && (
          <button
            type='button'
            onClick={onCapa}
            title={capaCount === 1 ? 'Open the CAPA' : `Open the latest of ${capaCount} CAPAs`}
            aria-label='CAPA'
            className={cn(ICON_BUTTON, 'w-auto gap-1 bg-rose-600 px-2 text-white hover:opacity-90')}
          >
            <ClipboardCheck className='h-4 w-4' />
            {capaCount > 1 && <span className='text-xs font-semibold'>{capaCount}</span>}
          </button>
        )}
        {prefs.show.auditTrail && access.canReadAudit && (
          <Button variant='ghost' size='sm' className='ml-auto h-7 gap-1.5 px-2 text-xs' onClick={onAudit}>
            <ScrollText className='h-3.5 w-3.5' />
            Audit Trail
          </Button>
        )}
      </footer>
    </article>
  )
}
